import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import writeXlsxFile, { Cell, SheetData } from 'write-excel-file';

import { useLocalize } from '@treams/treams-hooks';
import { DownloadIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	ITimeMachine,
	Modal,
	Paragraph,
	Popover,
	Progress as ProgressBar,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import { tableCellValue } from './AnalyticsMetricDetailsTable';

const Progress = styled(Box)`
	color: ${tokens.text.secondary};
`;

const columnWidths: Record<string, number | number[]> = {
	fraction: [8, 8, 8],
	user: 20,
	department: 20,
	departments: 20,
	managers: 20,
	title: 30,
	feedbackTitle: 30,
	createdAt: 20,
	feedbackGivenCount: 12,
	feedbackAnsweredCount: 12,
	feedbackRequestCount: 12,
	momentCount: 12,
	momentCompletedCount: 12,
	competency: 30,
};

export default function AnalyticsMetricDetailsExport(props: {
	id: string;
	filters: graphql.AnalyticsFiltersV2;
	type: graphql.AnalyticsType;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [fetchTable] = graphql.useAnalyticsMetricDetailsTableLazyQuery();
	const [fetchDetails] = graphql.useAnalyticsMetricDetailsLazyQuery();
	const [generating, setGenerating] = useState(false);
	const [generationProgress, setGenerationProgress] = useState(0);

	type TableAnalyticsData = {
		id: string;
		title?: string | null;
		data: {
			headers: graphql.AnalyticsDetailHeader[];
			rows: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>[];
		};
	};

	const fetchAllTables = async (
		id: string,
		filters: graphql.AnalyticsFiltersV2,
		type: graphql.AnalyticsType,
	) => {
		const details = await fetchDetails({
			variables: {
				id,
				filters,
				type,
			},
		});

		const tableList = details.data?.analytics.detailTableList || [];

		const tables: TableAnalyticsData[] = [];
		for (let i = 0; i < tableList.length; i++) {
			const table = tableList[i];
			const data = await fetchAllRowsForTable(id, filters, type, table.id, (progress) => {
				const totalProgress = i / tableList.length + progress / tableList.length;
				setGenerationProgress(totalProgress);
			});
			tables.push({
				id: table.id,
				title: table.title,
				data,
			});
		}

		return tables;
	};

	const fetchAllRowsForTable = async (
		id: string,
		filters: graphql.AnalyticsFiltersV2,
		type: graphql.AnalyticsType,
		tableID: string,
		progressCallback: (progressPercentage: number) => void,
	) => {
		const maxPages = 99999;
		let currentPageIndex = 0;
		let firstPageHeaders: graphql.AnalyticsDetailHeader[] = [];
		const allRows: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>[] = [];
		while (currentPageIndex < maxPages) {
			try {
				const fetchResult = await fetchTable({
					variables: {
						id,
						tableID,
						filters,
						type,
						pageIndex: currentPageIndex,
						pageSize: 30,
					},
				});
				const page = (fetchResult.data?.analytics.detail as graphql.AnalyticsDetail) || undefined;
				firstPageHeaders = page.headers;
				const rows =
					(page?.rows as graphql.AnalyticsDetailRow[])?.map((row) => {
						return Object.fromEntries(row.columns.map((column) => [column.key, column.value]));
					}) ?? [];

				const isLastPage = !page || page.rows.length === 0;
				if (isLastPage) {
					break;
				}

				allRows.push(...rows);
				currentPageIndex++;
				const progress = page ? allRows.length / page.totalRows : 0;
				progressCallback(progress);
			} catch {
				throw new Error(
					'An error occurred while generating export. Please try again or contact support.',
				);
			}
		}

		if (currentPageIndex >= maxPages) {
			throw new Error('Max pages reached for export');
		}

		progressCallback(1);
		return {
			headers: firstPageHeaders,
			rows: allRows,
		};
	};

	const generateExcelSheet = async (
		params: {
			id: string;
			filters: graphql.AnalyticsFiltersV2;
			type: graphql.AnalyticsType;
		},
		period: string,
		table: TableAnalyticsData,
	) => {
		const allDetails = table.data;
		const hasFraction = table.data.headers.some((header) => header.type === 'fraction');

		const columnWrap: Record<string, boolean> = {
			managers: true,
			departments: true,
			department: true,
		};

		const headers: SheetData = [
			allDetails.headers.flatMap((header) => {
				const headerValue = header.label || t(`analytics.metric.details.${header.key}`);

				if (header.type === 'fraction') {
					return [
						{
							fontWeight: 'bold',
							backgroundColor: '#eeeeee',
							bottomBorderStyle: !hasFraction ? 'thick' : undefined,
							value: headerValue,
							span: 3,
						},
						null,
						null,
					];
				}

				return [
					{
						value: headerValue,
						fontWeight: 'bold',
						backgroundColor: '#eeeeee',
						bottomBorderStyle: 'thick',
						rowSpan: hasFraction ? 2 : 1,
						span: 1,
						wrap: true,
						width: columnWidths[header.key] as number | undefined,
					},
				];
			}),
			...(hasFraction
				? [
						allDetails.headers.flatMap((header) => {
							if (header.type === 'fraction') {
								return [
									{
										value: t('analytics.export.columns.fraction.numerator'),
										fontWeight: 'bold',
										backgroundColor: '#eeeeee',
										bottomBorderStyle: 'thick',
									},
									{
										value: t('analytics.export.columns.fraction.denominator'),
										fontWeight: 'bold',
										backgroundColor: '#eeeeee',
										bottomBorderStyle: 'thick',
									},
									{
										value: t('analytics.export.columns.fraction.percentage'),
										fontWeight: 'bold',
										backgroundColor: '#eeeeee',
										bottomBorderStyle: 'thick',
									},
								] as Cell;
							}

							return [null];
						}),
					]
				: []),
		];

		const rows: SheetData = allDetails.rows.map((row) => {
			return allDetails.headers.flatMap((header) => {
				const value = tableCellValue(localize, row, header);

				if (header.type === 'fraction') {
					if (
						typeof value.excel?.value !== 'object' ||
						!('value' in value.excel.value) ||
						!('total' in value.excel.value)
					) {
						return [null, null, null];
					}

					const total = value.excel.value.total;

					return [
						{
							type: Number,
							value: value.excel.value.value,
						},
						{
							type: Number,
							value: total,
						},
						{
							type: Number,
							value: total > 0 ? value.excel.value.value / total : 0,
							format: '0.0%',
						},
					];
				}

				const wrap = columnWrap[header.key];

				if (value.excel) {
					return [
						{
							type: value.excel.type,
							value: value.excel.value ?? value.rawValue,
							format: value.excel.format,
							wrap,
						},
					] as Cell[];
				}

				return [
					{
						value: value.rawValue,
						wrap,
					},
				];
			});
		});

		return {
			headers,
			rows,
		};
	};

	const generateValidSheetTitle = (sheetIndex: number, tableTitle?: string | null) => {
		let title = (tableTitle || `Sheet ${sheetIndex + 1}`)
			// eslint-disable-next-line no-useless-escape
			.replace(/[\[\]\/\\:*?]/g, ' '); // removes characters not allowed in sheet names

		if (title.length > 29) {
			title = `${title.substring(0, 25)}.. ${sheetIndex}`;
		}

		return title;
	};

	const generateExcelFile = async (
		params: {
			id: string;
			filters: graphql.AnalyticsFiltersV2;
			type: graphql.AnalyticsType;
		},
		period: string,
	) => {
		setGenerationProgress(0);
		const allTables = await fetchAllTables(params.id, params.filters, params.type);

		const sheets: SheetData[] = [];
		const sheetNames: string[] = [];
		for (const table of allTables) {
			const { headers, rows } = await generateExcelSheet(params, period, table);
			sheets.push([...headers, ...rows]);

			const title = generateValidSheetTitle(sheetNames.length, table.title);
			sheetNames.push(title);
		}

		await writeXlsxFile(sheets, {
			sheets: sheetNames,
			fileName: `${t(`analytics.metric.details.${params.id}`)} - ${period}`,
			columns: allTables.map((table) => {
				return table.data.headers.flatMap((header) => {
					if (header.type === 'fraction') {
						const widths = columnWidths['fraction'] as number[] | undefined;

						if (!widths) {
							return [{}, {}, {}];
						}

						return widths.map((width) => ({ width }));
					}

					const width = columnWidths[header.key] as number | undefined;

					return [{ width }];
				});
			}),
		});
	};

	const onClick = async () => {
		const period =
			props.timeMachine.interval === 'month'
				? moment(props.timeMachine.current[0]).format('MMMM YYYY')
				: props.timeMachine.interval === 'quarter'
					? `Q${moment(props.timeMachine.current[0]).format('Q YYYY')}`
					: props.timeMachine.interval === 'year'
						? moment(props.timeMachine.current[0]).format('YYYY')
						: props.timeMachine.current[0].toString();

		setGenerating(true);
		try {
			await generateExcelFile({ id: props.id, filters: props.filters, type: props.type }, period);
		} catch {
			alert(t('analytics.export.error'));
		} finally {
			setGenerating(false);
		}
	};

	return (
		<>
			<Button
				type="secondary"
				icon={<DownloadIcon />}
				title={t('analytics.metric.downloadButton')}
				onClick={onClick}
			/>
			{generating && (
				<Popover visible center zIndex={10}>
					<Modal>
						<Modal.Header title={t('analytics.export.modal.title')} />

						<Modal.Contents>
							<Flex alignItems="center" flexDirection="column">
								<Spinner mb={3} />

								<Paragraph>{t('analytics.export.modal.explanation')}</Paragraph>
								<Flex alignItems="center" mt={3}>
									<Box>
										<ProgressBar value={generationProgress} />
									</Box>
									<Progress ml={2}>{(generationProgress * 100).toFixed(0)}%</Progress>
								</Flex>
							</Flex>
						</Modal.Contents>
					</Modal>
				</Popover>
			)}
		</>
	);
}
